import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Alert, AlertDescription } from '../components/ui/alert';
import api from '../utils/api';

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, user, loading: authLoading } = useAuth();

  const resetToken = searchParams.get('token');
  const initialMode = resetToken ? 'reset' : 'login';

  const [mode, setMode] = useState(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const errorRef = useRef(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (user && !authLoading) {
      navigate('/app/matters');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    setError('');
    setSuccess('');
  }, [mode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (mode === 'reset') {
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters');
        return false;
      }
    }

    if (!formData.email && mode !== 'reset') {
      setError('Email is required');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (mode === 'login') {
        const success = await login(formData.email, formData.password);
        if (success) {
          navigate('/app/matters');
        }
      } else if (mode === 'forgot') {
        await api.post('/auth/forgot-password', { email: formData.email });
        setSuccess('If an account exists with this email, you will receive reset instructions.');
        setFormData(prev => ({ ...prev, email: '' }));
      } else if (mode === 'reset') {
        await api.post('/auth/reset-password', {
          token: resetToken,
          new_password: formData.password
        });
        setSuccess('Password reset successfully! You can now log in.');
        setTimeout(() => {
          setMode('login');
          navigate('/auth');
        }, 2000);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'An error occurred. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    height: '48px',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    paddingLeft: '44px',
    paddingRight: '16px',
    fontSize: '15px',
    fontFamily: 'Inter, sans-serif',
    color: '#1e293b',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    backgroundColor: '#ffffff',
  };

  const inputFocusHandler = (e) => {
    e.target.style.borderColor = '#f59e0b';
    e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.12)';
  };

  const inputBlurHandler = (e) => {
    e.target.style.borderColor = '#e2e8f0';
    e.target.style.boxShadow = 'none';
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#fefdfb' }}>
        <div style={{ color: '#64748b', fontFamily: 'Inter, sans-serif' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#fefdfb', minHeight: '100vh' }}>
      <style>{`
        @media (max-width: 480px) {
          .auth-outer { align-items: flex-start !important; padding-top: 48px !important; }
          .auth-card { padding: 24px 28px !important; border-radius: 12px !important; }
          .auth-logo { font-size: 28px !important; }
          .auth-header { font-size: 22px !important; }
        }
      `}</style>

      <div
        className="auth-outer"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px 16px',
        }}
      >
        <div style={{ width: '100%', maxWidth: '420px' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div
              className="auth-logo"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '32px',
                fontWeight: 400,
                color: '#1e293b',
                cursor: 'pointer',
                marginBottom: '8px',
              }}
              onClick={() => navigate('/')}
            >
              <span style={{ textDecoration: 'underline', textDecorationColor: '#B35309', textUnderlineOffset: '4px' }}>b</span>arrow.
            </div>
            <p style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '15px',
              fontWeight: 400,
              color: '#64748b',
            }}>
              Legal Document Intelligence
            </p>
          </div>

          <div
            className="auth-card"
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              padding: '36px',
              border: '1px solid #f1f5f9',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.03)',
            }}
          >
            {mode === 'login' && (
              <h2
                className="auth-header"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '24px',
                  fontWeight: 400,
                  color: '#1e293b',
                  marginBottom: '24px',
                  textAlign: 'center',
                }}
              >
                Welcome back
              </h2>
            )}

            {mode === 'forgot' && (
              <div style={{ marginBottom: '24px' }}>
                <h2
                  className="auth-header"
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: '24px',
                    fontWeight: 400,
                    color: '#1e293b',
                    marginBottom: '8px',
                  }}
                >
                  Forgot Password
                </h2>
                <p style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#64748b',
                }}>
                  Enter your email and we'll send you reset instructions.
                </p>
              </div>
            )}

            {mode === 'reset' && (
              <div style={{ marginBottom: '24px' }}>
                <h2
                  className="auth-header"
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: '24px',
                    fontWeight: 400,
                    color: '#1e293b',
                    marginBottom: '8px',
                  }}
                >
                  Reset Password
                </h2>
                <p style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#64748b',
                }}>
                  Enter your new password below.
                </p>
              </div>
            )}

            {error && (
              <Alert className="mb-4 bg-red-50 border-red-200" ref={errorRef}>
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800" data-testid="error-message">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert className="mb-4 bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800" data-testid="success-message">
                  {success}
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              {mode !== 'reset' && (
                <div style={{ marginBottom: '18px' }}>
                  <label style={{ display: 'block', fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 500, color: '#1e293b', marginBottom: '6px' }}>
                    Email
                  </label>
                  <div style={{ position: 'relative' }}>
                    <Mail style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '18px', height: '18px', color: '#94a3b8' }} />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@lawfirm.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      onFocus={inputFocusHandler}
                      onBlur={inputBlurHandler}
                      style={inputStyle}
                      data-testid="email-input"
                    />
                  </div>
                </div>
              )}

              {mode !== 'forgot' && (
                <div style={{ marginBottom: mode === 'reset' ? '18px' : '24px' }}>
                  <label style={{ display: 'block', fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 500, color: '#1e293b', marginBottom: '6px' }}>
                    {mode === 'reset' ? 'New Password' : 'Password'}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <Lock style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '18px', height: '18px', color: '#94a3b8' }} />
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleInputChange}
                      onFocus={inputFocusHandler}
                      onBlur={inputBlurHandler}
                      style={{ ...inputStyle, paddingRight: '44px' }}
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '14px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {showPassword
                        ? <EyeOff style={{ width: '18px', height: '18px', color: '#94a3b8' }} />
                        : <Eye style={{ width: '18px', height: '18px', color: '#94a3b8' }} />
                      }
                    </button>
                  </div>
                  {mode === 'reset' && (
                    <p style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '13px',
                      color: '#94a3b8',
                      marginTop: '6px',
                    }}>
                      Min 8 characters with uppercase, lowercase, and number
                    </p>
                  )}
                </div>
              )}

              {mode === 'reset' && (
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 500, color: '#1e293b', marginBottom: '6px' }}>
                    Confirm New Password
                  </label>
                  <div style={{ position: 'relative' }}>
                    <Lock style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '18px', height: '18px', color: '#94a3b8' }} />
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      onFocus={inputFocusHandler}
                      onBlur={inputBlurHandler}
                      style={inputStyle}
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  height: '48px',
                  backgroundColor: loading ? '#334155' : '#1e293b',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '10px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => { if (!loading) e.target.style.backgroundColor = '#334155'; }}
                onMouseLeave={(e) => { if (!loading) e.target.style.backgroundColor = '#1e293b'; }}
                data-testid="submit-button"
              >
                {loading ? 'Please wait...' : (
                  mode === 'login' ? 'Sign In' :
                  mode === 'forgot' ? 'Send Reset Link' :
                  'Reset Password'
                )}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              {mode === 'login' && (
                <>
                  <button
                    type="button"
                    onClick={() => setMode('forgot')}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '14px',
                      color: '#475569',
                      cursor: 'pointer',
                      padding: 0,
                      transition: 'color 0.2s',
                    }}
                    onMouseEnter={(e) => { e.target.style.color = '#1e293b'; e.target.style.textDecoration = 'underline'; }}
                    onMouseLeave={(e) => { e.target.style.color = '#475569'; e.target.style.textDecoration = 'none'; }}
                    data-testid="forgot-password-link"
                  >
                    Forgot your password?
                  </button>

                  <div style={{ borderTop: '1px solid #e2e8f0', margin: '24px 0' }} />

                  <p style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    color: '#475569',
                  }}>
                    Don't have an account?{' '}
                    <Link to="/register" style={{ color: '#B35309', fontWeight: 500, textDecoration: 'none' }}>
                      Sign Up
                    </Link>
                  </p>
                </>
              )}

              {(mode === 'forgot' || mode === 'reset') && (
                <button
                  type="button"
                  onClick={() => {
                    setMode('login');
                    navigate('/auth');
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    color: '#475569',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                  onMouseEnter={(e) => { e.target.style.color = '#1e293b'; e.target.style.textDecoration = 'underline'; }}
                  onMouseLeave={(e) => { e.target.style.color = '#475569'; e.target.style.textDecoration = 'none'; }}
                >
                  Back to Sign In
                </button>
              )}
            </div>
          </div>

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <p style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '13px',
              color: '#64748b',
              lineHeight: 1.5,
            }}>
              By continuing, you agree to our{' '}
              <a href="https://barrow.legal/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color: '#475569', textDecoration: 'underline' }}>Terms of Service</a>
              {' '}and{' '}
              <a href="https://barrow.legal/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#475569', textDecoration: 'underline' }}>Privacy Policy</a>.
            </p>
            <p style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '12px',
              color: '#94a3b8',
              marginTop: '8px',
            }}>
              Barrow is not a law firm and does not provide legal advice.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
